<template>
  <div class="account-pages my-5">
    <div class="container">
      <div class="justify-content-center auth-container">
        <div class="card">
          <div class="card-body p-0">
            <div class="p-5 text-center">
              <div v-if="loading">
                <b-spinner variant="primary" class="mb-3"></b-spinner>
                <h5>Signing you in from Nitro Booster...</h5>
                <p class="text-muted">Please wait while we set up your account.</p>
              </div>
              
              <div v-else-if="error">
                <feather type="alert-circle" class="text-danger mb-3" size="48"></feather>
                <h5 class="text-danger">Sign In Failed</h5>
                <p class="text-muted">{{ errorMessage }}</p>
                <router-link :to="{ name: 'login' }" class="btn btn-primary mt-3">
                  Go to Login
                </router-link>
              </div>
              
              <div v-else-if="success">
                <feather type="check-circle" class="text-success mb-3" size="48"></feather>
                <h5 class="text-success">Welcome!</h5>
                <p class="text-muted">Redirecting to your dashboard...</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Bundle Info Card -->
        <div v-if="!error" class="card mt-3">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <div class="bg-primary-subtle p-2 rounded">
                  <feather type="package" class="text-primary" size="24"></feather>
                </div>
              </div>
              <div class="flex-grow-1 ms-3 ml-3">
                <h6 class="mb-1">Customer Engine: Nitro Booster</h6>
                <p class="text-muted mb-0 small">
                  Your account is connected to the Nitro Booster bundle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      error: false,
      success: false,
      errorMessage: '',
    }
  },

  mounted() {
    this.handleCallback()
  },

  methods: {
    handleCallback() {
      const query = this.$route.query
      
      // Check for error from API redirect
      if (query.error) {
        this.loading = false
        this.error = true
        this.errorMessage = this.getErrorMessage(query.error)
        return
      }
      
      // Check for access token from bundle SSO
      const accessToken = query.access_token
      const bundleSSO = query.bundle_sso === 'true'
      const setupRequired = query.setup_required === 'true'
      
      if (!accessToken) {
        this.loading = false
        this.error = true
        this.errorMessage = 'No authentication token received.'
        return
      }
      
      // Store the token and set up axios
      this.$store.commit('auth/bundleSSOSuccess', { 
        access_token: accessToken,
        bundle_sso: bundleSSO 
      })
      // Strip the token from the URL. It arrives as ?access_token=... so it lands in
      // browser history, server access logs and any Referer header. Nothing reads
      // $route.query after this point, so removing it changes no behaviour.
      window.history.replaceState({}, '', '/auth/bundle-callback')

      
      // If setup is required, store flag for the welcome modal
      if (setupRequired) {
        localStorage.setItem('nitro_setup_required', 'true')
        console.log('[bundle-sso] First-time user, setup required')
      }
      
      // Fetch user profile
      this.$store
        .dispatch('auth/getProfile')
        .then((user) => {
          console.log('[bundle-sso] Profile loaded:', user)
          this.loading = false
          this.success = true
          
          // Redirect to appropriate dashboard after a short delay
          setTimeout(() => {
            try {
              const role = user?.role || 'Agency'
              const targetPath = '/' + role.toLowerCase()
              console.log('[bundle-sso] Redirecting to:', targetPath, 'user:', user)
              
              if (role === 'Agency' && user?.agency?.twilioConfig?.status === 'Trial') {
                this.$router.push(targetPath + '?warning=trial')
              } else {
                this.$router.push(targetPath).catch(err => {
                  console.error('[bundle-sso] Router push failed:', err)
                  // Fallback: use window.location
                  window.location.href = targetPath
                })
              }
            } catch (err) {
              console.error('[bundle-sso] Redirect error:', err)
              // Ultimate fallback
              window.location.href = '/agency'
            }
          }, 1500)
        })
        .catch((error) => {
          this.loading = false
          this.error = true
          this.errorMessage = 'Failed to load your profile. Please try logging in again.'
          console.error('[bundle-sso] Profile fetch error:', error)
        })
    },
    
    getErrorMessage(errorCode) {
      const messages = {
        'missing_token': 'No authentication token was provided.',
        'invalid_token': 'The authentication token is invalid or has expired.',
        'token_reused': 'This sign-in link has already been used. Please request a new one.',
        'default': 'An unknown error occurred. Please try again.',
      }
      return messages[errorCode] || messages['default']
    },
  },
}
</script>

<style lang="scss" scoped>
.auth-container {
  width: 460px;
  max-width: 100%;
  margin: 0 auto;
  margin-top: 15vh;
}

.bg-primary-subtle {
  background-color: rgba(var(--bs-primary-rgb), 0.1);
}
</style>
